import { logger } from '../../utils';

export const STORAGE_EVENT_KEY = 'storage';

// ENB-2330 (F-13): keys sensitive enough that a cross-tab-persisted copy is a
// bigger risk than re-authenticating on tab close. PKCE keys (code_verifier,
// oauth_state, oauth_nonce) are deliberately excluded — see setEphemeralData's
// JSDoc in the native/shared index.ts for the password-reset flow reasoning.
const SENSITIVE_KEYS = new Set(['credentials', 'user_profile']);

function pickStore(key: string): Storage {
  return SENSITIVE_KEYS.has(key) ? window.sessionStorage : window.localStorage;
}

// Sweeps any sensitive key left in localStorage into sessionStorage. Runs on
// every module load, not just once — a rolling deploy can leave an old-bundle
// tab writing a refreshed token back to localStorage (via the old
// AsyncStorage-backed adapter) after a new-bundle tab already swept once; a
// one-shot marker would leave that later write stranded in localStorage
// forever. The loop is idempotent (copy-if-missing, then always remove) and
// cheap, so gating it behind a "done" marker bought nothing but risk.
//
// Each key is isolated: a throw while copying one key must not skip its
// removal, nor abort the keys after it — otherwise the very failure path this
// guards would strand a refresh token in localStorage, which is the outcome
// this whole migration exists to prevent.
function sweepSensitiveKeysFromLocalStorage(): void {
  for (const key of SENSITIVE_KEYS) {
    try {
      const legacy = window.localStorage.getItem(key);
      if (legacy !== null && window.sessionStorage.getItem(key) === null) {
        window.sessionStorage.setItem(key, legacy);
      }
    } catch (e) {
      // Copy failed (quota exceeded, private mode, storage disabled).
      // Non-fatal — we still drop the localStorage copy below, so the user
      // simply signs in again, which the release note already covers.
      logger.error('ENB-2330 storage migration failed', e);
    } finally {
      try {
        window.localStorage.removeItem(key);
      } catch (e) {
        logger.error('ENB-2330 storage cleanup failed', e);
      }
    }
  }
}

sweepSensitiveKeysFromLocalStorage();

class StorageService {
  setData = async (key: string, value: unknown) => {
    try {
      pickStore(key).setItem(key, JSON.stringify(value));
      if (window && window.dispatchEvent && CustomEvent) {
        window.dispatchEvent(
          new CustomEvent(STORAGE_EVENT_KEY, { detail: { key, value } })
        );
      }
    } catch (e) {
      logger.error('Set data failed', e);
    }
  };

  getData = async <Data = string>(key: string): Promise<Data | void> => {
    try {
      const value = pickStore(key).getItem(key);
      if (value !== null) {
        return JSON.parse(value);
      }
    } catch (e) {
      logger.error('Get stored data failed', e);
    }
  };

  /**
   * Store short-lived OAuth values (code_verifier, oauth_state, oauth_nonce).
   * Uses localStorage (not sessionStorage) so values survive cross-origin
   * redirect chains such as password-reset flows, where sessionStorage may
   * be unavailable in a new tab or cleared by the browser (e.g. Safari ITP).
   * Security is maintained by explicitly clearing these values immediately
   * after the PKCE code exchange completes.
   */
  setEphemeralData = async (key: string, value: unknown): Promise<void> => {
    try {
      if (value === null || value === undefined) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, JSON.stringify(value));
      }
    } catch (e) {
      logger.error('setEphemeralData failed', e);
      throw e;
    }
  };

  getEphemeralData = async <Data = string>(key: string): Promise<Data | undefined> => {
    try {
      const value = window.localStorage.getItem(key);
      if (value !== null) {
        return JSON.parse(value) as Data;
      }
      return undefined;
    } catch (e) {
      logger.error('getEphemeralData failed', e);
      return undefined;
    }
  };
}

export default StorageService;
export const storageInstance = new StorageService();
